import { Button, Menu, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import React, { useState } from 'react'
import * as XLSX from 'xlsx';
import BootstrapTooltip from '../../../components/tooltip';
import { logoBase64 } from '../../../assets/images/dashboard/logo_base64';
import { leptonCheckLogoBase64 } from '../../../assets/images/dashboard/leptonCheck-logo_base64';

const StatsTab = ({ devices, userRoute, userSchedules, rows, vehicleFilter, setVehicleFilter, checkpointFilter, setCheckpointFilter, statusFilter, setStatusFilter, selectStyles, tableCellHeaderStyle, tableCellBodyStyle }) => {

    const filteredRows = rows.filter(row => {
        return (
            (vehicleFilter === '' || row.vehicle === vehicleFilter) &&
            (checkpointFilter === '' || row.checkpoint === checkpointFilter) &&
            (statusFilter === '' || row.status === statusFilter)
        );
    });

    const parseDifference = (difference) => {
        if (!difference) return 0;
        const match = difference.match(/(-?\d+)\s*min.*?(\d+)\s*seg/);
        if (!match) return 0;
        const minutes = parseInt(match[1], 10);
        const seconds = parseInt(match[2], 10);
        return minutes * 60 + seconds;
    };

    const calculateStatistics = () => {
        const totalEvents = filteredRows.length;

        const stats = {
            totalEvents,
            totalStatusEvents: 0,
            punctualityRate: 0,
            onTime: { count: 0, totalTime: 0, maxDifference: 0 },
            delayed: { count: 0, totalTime: 0, maxDifference: 0 },
            early: { count: 0, totalTime: 0, maxDifference: 0 }
        };

        filteredRows.forEach(row => {
            const diffSeconds = parseDifference(row.difference); // Convierte diferencia a segundos

            if (row.status === 'En tiempo') {
                stats.onTime.count++;
                stats.onTime.totalTime += diffSeconds;
                stats.onTime.maxDifference = Math.max(stats.onTime.maxDifference, diffSeconds);
            } else if (row.status === 'Atrasado') {
                stats.delayed.count++;
                stats.delayed.totalTime += diffSeconds;
                stats.delayed.maxDifference = Math.max(stats.delayed.maxDifference, diffSeconds);
            } else if (row.status === 'Adelantado') {
                stats.early.count++;
                stats.early.totalTime += diffSeconds;
                stats.early.maxDifference = Math.max(stats.early.maxDifference, diffSeconds);
            }
        });

        // Calcular tasa de puntualidad
        stats.punctualityRate = totalEvents ? ((stats.onTime.count / totalEvents) * 100).toFixed(2) : 0;

        stats.totalStatusEvents = totalEvents ? (stats.onTime.count + stats.delayed.count + stats.early.count) : 0;

        return stats;
    };

    const filteredDevices = devices.filter(device =>
        rows.some(row => row.vehicle === device.label)
    );

    const stats = calculateStatistics();

    const generateVehicleSummary = () => {
        const vehicleStats = {};

        filteredRows.forEach(row => {
            if (!vehicleStats[row.vehicle]) {
                vehicleStats[row.vehicle] = {
                    earlyTime: 0,
                    onTimeTime: 0,
                    delayedTime: 0,
                    schedule: userSchedules.find(schedule => schedule.vehicleId === filteredDevices.find(device => device.label === row.vehicle)?.id)?.name || "Sin horario"
                };
            }

            const diffSeconds = parseDifference(row.difference);

            if (row.status === 'Adelantado') {
                vehicleStats[row.vehicle].earlyTime += diffSeconds;
            } else if (row.status === 'En tiempo') {
                vehicleStats[row.vehicle].onTimeTime += diffSeconds;
            } else if (row.status === 'Atrasado') {
                vehicleStats[row.vehicle].delayedTime += diffSeconds;
            }
        });

        return Object.keys(vehicleStats).map(vehicle => ({
            vehicle,
            schedule: vehicleStats[vehicle].schedule,
            onTimeTime: vehicleStats[vehicle].onTimeTime,
            earlyTime: vehicleStats[vehicle].earlyTime,
            delayedTime: vehicleStats[vehicle].delayedTime,
            totalTime: vehicleStats[vehicle].earlyTime + vehicleStats[vehicle].onTimeTime + vehicleStats[vehicle].delayedTime
        }));
    };

    const formatTimeToText = (totalSeconds) => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${String(hours).padStart(2, '0')} h ${String(minutes).padStart(2, '0')} min ${String(seconds).padStart(2, '0')} seg`;
    };

    const DonutChart = ({ stats }) => {
        const validTotal = stats.onTime.count + stats.delayed.count + stats.early.count; // Solo eventos con estatus válido
        const radius = 50; // Radio del círculo externo
        const thickness = 15; // Grosor del anillo
        const center = 60; // Centro del SVG

        if (validTotal === 0) {
            return <p style={{ margin: 'auto 0', textAlign: "center", fontSize: "14px", color: "#666" }}>No se encontraron registros.</p>;
        }

        // Calcula los porcentajes de cada estado respecto al total válido
        const percentages = {
            onTime: stats.onTime.count / validTotal,
            delayed: stats.delayed.count / validTotal,
            early: stats.early.count / validTotal
        };

        // Colores de cada estado
        const colors = {
            onTime: "var(--color-success)",  // Verde (En tiempo)
            delayed: "var(--color-danger)", // Rojo (Atrasado)
            early: "var(--color-warning)"    // Naranja (Adelantado)
        };

        const getArcPath = (startAngle, endAngle) => {
            const largeArc = endAngle - startAngle > 180 ? 1 : 0;
            const radians = (angle) => (angle - 90) * (Math.PI / 180);

            const x1 = center + radius * Math.cos(radians(startAngle));
            const y1 = center + radius * Math.sin(radians(startAngle));
            const x2 = center + radius * Math.cos(radians(endAngle));
            const y2 = center + radius * Math.sin(radians(endAngle));

            return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${center} ${center} Z`;
        };

        let startAngle = 0;
        let fullSegment = null;

        const arcs = Object.keys(percentages).map((key) => {
            if (percentages[key] === 1) {
                fullSegment = key; // Si este segmento es 100%, lo guardamos
                return null;
            }

            const sliceAngle = percentages[key] * 360;
            const endAngle = startAngle + sliceAngle;
            const path = getArcPath(startAngle, endAngle);

            // Calcular posición del texto dentro del segmento
            const midAngle = startAngle + sliceAngle / 2;
            const radians = (angle) => (angle - 90) * (Math.PI / 180);
            const textX = center + (radius - thickness / 2) * Math.cos(radians(midAngle));
            const textY = center + (radius - thickness / 2) * Math.sin(radians(midAngle));

            startAngle = endAngle;
            return { path, color: colors[key], label: key, percentage: (percentages[key] * 100).toFixed(1), textX, textY };
        }).filter(Boolean);

        return (
            <svg className='donut_chart_svg' width="200" height="200" viewBox="0 0 120 120">
                {/* Fondo del anillo */}
                <circle cx={center} cy={center} r={radius} fill="none" stroke="var(--color-dark-light)" strokeWidth={thickness + 2} />
                {/* Arcos de la gráfica */}
                {fullSegment ? (
                    <circle cx={center} cy={center} r={radius} fill={colors[fullSegment]} stroke="var(--color-white)" strokeWidth="2" />
                ) : (
                    // Dibujar los arcos de la gráfica
                    arcs.map((arc, index) => (
                        <path key={index} d={arc.path} fill={arc.color} stroke="var(--color-white)" strokeWidth="2" />
                    ))
                )}
                {/* Círculo interno para hacer la dona */}
                <circle cx={center} cy={center} r={radius - thickness} fill="var(--color-white)" />
                {/* Texto en el centro */}
                <text x={center} y={center - 5} textAnchor="middle" fontSize="12" fill="var(--color-dark)" fontWeight="bold">
                    {validTotal}
                </text>
                <text x={center} y={center + 10} textAnchor="middle" fontSize="10" fill="var(--color-dark)">
                    Eventos
                </text>
                <text x={center} y={center + 18} textAnchor="middle" fontSize="5" fill="var(--color-dark)">
                    (con coincidencias)
                </text>
            </svg>
        );
    };

    const vehicleData = generateVehicleSummary();

    const COLOR_MAP = {
        'var(--color-success)': '#1B9C85',   // Ajusta al color real de tu tema
        'var(--color-danger)': '#FF0060',
        'var(--color-warning)': '#F7D060',
        'var(--color-dark-light)': 'rgba(224, 224, 224, 1)',
        'var(--color-white)': '#fff',
        'var(--color-dark)': '#363949',
    };

    function svgReplaceColors(svgStr) {
        let out = svgStr;
        for (const [cssVar, hex] of Object.entries(COLOR_MAP)) {
            out = out.replaceAll(cssVar, hex);
        }
        return out;
    }


    // Estado para el menú de descarga
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClickDownload = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Exportar PDF
    const handleExportPDF = async () => {
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'pt',
            format: 'A4'
        });

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const headerHeight = 55;
        const logoWidth = 100;
        const logoHeight = 35;
        const marginX = 40;
        const footerMarginY = 18;
        const reportName = "Reporte de flotas por estadísticas";

        const footerTexts = [];

        function drawCorporateHeader() {
            if (logoBase64) {
                doc.addImage(logoBase64, 'PNG', marginX, 12, logoWidth, logoHeight);
            }
            // Dibuja el segundo logo (header derecho)
            const headerImgWidth = 165;  // ajusta según tamaño de tu nueva imagen
            const headerImgHeight = 35;  // ajusta según tamaño de tu nueva imagen
            if (leptonCheckLogoBase64) {
                doc.addImage(
                    leptonCheckLogoBase64,
                    'PNG',
                    pageWidth - marginX - headerImgWidth,
                    12,
                    headerImgWidth,
                    headerImgHeight
                );
            }
        }

        // --- HEADER CORPORATIVO ---
        drawCorporateHeader();

        // --- Título general --- (más abajo)
        doc.setFontSize(18);
        doc.text('Reporte de flotas por estadísticas', 40, 80); // Y=70 para que no se encime

        // --- Resumen (aún más abajo)
        doc.setFontSize(12);
        let y = 105;
        doc.text(`Total de eventos: ${stats.totalEvents}`, 40, y);
        y += 18;
        doc.text(`Puntualidad: ${stats.punctualityRate}%`, 40, y);
        y += 28;

        // --- En tiempo ---
        doc.setFontSize(13);
        doc.text('En tiempo:', 40, y);
        y += 16;
        doc.setFontSize(12);
        doc.text(`Cantidad: ${stats.onTime.count} (${stats.totalStatusEvents > 0 ? (stats.onTime.count / stats.totalStatusEvents * 100).toFixed(2) + '%' : '- %'})`, 60, y);
        y += 16;
        doc.text(
            `Diferencia máxima: ${formatTimeToText(stats.onTime.maxDifference)}    Tiempo acumulado: ${formatTimeToText(stats.onTime.totalTime)}`,
            60, y
        );
        y += 20;

        // --- Adelantos ---
        doc.setFontSize(13);
        doc.text('Adelantos:', 40, y);
        y += 16;
        doc.setFontSize(12);
        doc.text(`Cantidad: ${stats.early.count} (${stats.totalStatusEvents > 0 ? (stats.early.count / stats.totalStatusEvents * 100).toFixed(2) + '%' : '- %'})`, 60, y);
        y += 16;
        doc.text(
            `Diferencia máxima: ${formatTimeToText(stats.early.maxDifference)}    Tiempo acumulado: ${formatTimeToText(stats.early.totalTime)}`,
            60, y
        );
        y += 20;

        // --- Atrasos ---
        doc.setFontSize(13);
        doc.text('Atrasos:', 40, y);
        y += 16;
        doc.setFontSize(12);
        doc.text(`Cantidad: ${stats.delayed.count} (${stats.totalStatusEvents > 0 ? (stats.delayed.count / stats.totalStatusEvents * 100).toFixed(2) + '%' : '- %'})`, 60, y);
        y += 16;
        doc.text(
            `Diferencia máxima: ${formatTimeToText(stats.delayed.maxDifference)}    Tiempo acumulado: ${formatTimeToText(stats.delayed.totalTime)}`,
            60, y
        );

        // --- Gráfica SVG a la derecha ---
        const svg = document.querySelector('.donut_chart_svg');
        if (svg) {
            let svgData = new XMLSerializer().serializeToString(svg);
            svgData = svgReplaceColors(svgData);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);

            const img = new window.Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = 320;
                canvas.height = 320;
                const ctx = canvas.getContext('2d');
                ctx.fillStyle = "#FFF";
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                const pngUrl = canvas.toDataURL('image/png');
                doc.addImage(pngUrl, 'PNG', 600, 85, 200, 200); // Y=85 para alinear con nuevo header

                // --- Tabla al final ---
                const table = document.querySelector('.MuiTable-root');
                const tableStartY = Math.max(y + 40, 320);
                if (table) autoTable(doc, {
                    html: table,
                    startY: tableStartY,
                    margin: { top: headerHeight },
                    didDrawPage: function (data) {
                        drawCorporateHeader();
                        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
                        footerTexts.push({
                            page: pageNumber,
                            y: pageHeight - footerMarginY
                        });
                        doc.setFontSize(10);
                        doc.setTextColor(100);
                        doc.text(reportName, marginX, pageHeight - footerMarginY, { align: 'left' });
                        doc.text(`Página ${pageNumber} de {total}`, pageWidth - marginX, pageHeight - footerMarginY, { align: 'right' });
                    }
                });

                // Corrige la paginación
                const totalPages = doc.internal.getNumberOfPages();
                for (let i = 1; i <= totalPages; i++) {
                    doc.setPage(i);
                    const footer = footerTexts[i - 1];
                    if (footer) {
                        doc.setFillColor(255, 255, 255);
                        doc.rect(marginX - 5, footer.y - 10, pageWidth - 2 * marginX + 10, 16, "F");
                        doc.setFontSize(10);
                        doc.setTextColor(100);
                        doc.text(reportName, marginX, footer.y, { align: 'left' });
                        doc.text(`Página ${i} de ${totalPages}`, pageWidth - marginX, footer.y, { align: 'right' });
                    }
                }

                doc.save('Reporte de flotas por estadísticas.pdf');
                URL.revokeObjectURL(url);
            };
            img.src = url;
            return;
        }

        // Si no hay gráfica SVG
        const table = document.querySelector('.MuiTable-root');
        if (table) autoTable(doc, {
            html: table,
            startY: Math.max(y + 40, 320),
            margin: { top: headerHeight },
            didDrawPage: function (data) {
                drawCorporateHeader();
                const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
                footerTexts.push({
                    page: pageNumber,
                    y: pageHeight - footerMarginY
                });
                doc.setFontSize(10);
                doc.setTextColor(100);
                doc.text(reportName, marginX, pageHeight - footerMarginY, { align: 'left' });
                doc.text(`Página ${pageNumber} de {total}`, pageWidth - marginX, pageHeight - footerMarginY, { align: 'right' });
            }
        });

        // Corrige la paginación
        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            const footer = footerTexts[i - 1];
            if (footer) {
                doc.setFillColor(255, 255, 255);
                doc.rect(marginX - 5, footer.y - 10, pageWidth - 2 * marginX + 10, 16, "F");
                doc.setFontSize(10);
                doc.setTextColor(100);
                doc.text(reportName, marginX, footer.y, { align: 'left' });
                doc.text(`Página ${i} de ${totalPages}`, pageWidth - marginX, footer.y, { align: 'right' });
            }
        }

        doc.save('Reporte de flotas por estadísticas.pdf');
    };

    // Exportar Excel
    const handleExportExcel = () => {
        const wb = XLSX.utils.book_new();

        // --- Resumen, cada dato en su propia fila (como lo tienes) ---
        const resumenData = [
            ["Total de eventos", stats.totalEvents],
            ["Puntualidad (%)", stats.punctualityRate],
            ["Eventos (con coincidencias)", stats.totalStatusEvents],
            [""],
            ["En tiempo:"],
            [
                `Cantidad: ${stats.onTime.count} (${stats.totalStatusEvents > 0 ? (stats.onTime.count / stats.totalStatusEvents * 100).toFixed(2) + '%' : '- %'})`
            ],
            [
                `Diferencia máxima: ${formatTimeToText(stats.onTime.maxDifference)}`
            ],
            [
                `Tiempo acumulado: ${formatTimeToText(stats.onTime.totalTime)}`
            ],
            [""],
            ["Adelantos:"],
            [
                `Cantidad: ${stats.early.count} (${stats.totalStatusEvents > 0 ? (stats.early.count / stats.totalStatusEvents * 100).toFixed(2) + '%' : '- %'})`
            ],
            [
                `Diferencia máxima: ${formatTimeToText(stats.early.maxDifference)}`
            ],
            [
                `Tiempo acumulado: ${formatTimeToText(stats.early.totalTime)}`
            ],
            [""],
            ["Atrasos:"],
            [
                `Cantidad: ${stats.delayed.count} (${stats.totalStatusEvents > 0 ? (stats.delayed.count / stats.totalStatusEvents * 100).toFixed(2) + '%' : '- %'})`
            ],
            [
                `Diferencia máxima: ${formatTimeToText(stats.delayed.maxDifference)}`
            ],
            [
                `Tiempo acumulado: ${formatTimeToText(stats.delayed.totalTime)}`
            ],
            [""]
        ];

        // --- Tabla por vehículo ---
        const vehicleSheetData = [
            [
                "Vehículo",
                "Horario",
                "Tiempo Adelantado",
                "Tiempo Puntual",
                "Tiempo Atrasado",
                "Tiempo Total"
            ],
            ...vehicleData.map(row => [
                row.vehicle,
                row.schedule,
                formatTimeToText(row.earlyTime),
                formatTimeToText(row.onTimeTime),
                formatTimeToText(row.delayedTime),
                formatTimeToText(row.totalTime)
            ])
        ];

        // --- Junta resumen y tabla horizontalmente ---
        const resumenRows = resumenData.length;
        const vehicleRows = vehicleSheetData.length;
        const maxRows = Math.max(resumenRows, vehicleRows);

        // Columna donde empieza la tabla de vehículos (después de resumen + separación)
        const resumenCols = Math.max(...resumenData.map(row => row.length));
        const tableStartCol = resumenCols + 2; // Puedes poner +1 o +2 para más separación

        // Construye la hoja
        const fullSheetData = [];
        for (let i = 0; i < maxRows; i++) {
            // Parte resumen (o vacío)
            const resumenPart = resumenData[i] || [];
            // Parte tabla (o vacío)
            const vehiclePart = vehicleSheetData[i] || [];
            // Junta en la fila: resumen a la izquierda, tabla a la derecha (desplazada)
            const row = [
                ...resumenPart,
                ...Array(tableStartCol - resumenPart.length).fill(""), // espacios vacíos
                ...vehiclePart
            ];
            fullSheetData.push(row);
        }
        // --- Ancho de columnas ---
        const ws = XLSX.utils.aoa_to_sheet(fullSheetData);
        ws['!cols'] = [
            { wch: 35 }, { wch: 10 }, // resumen
            { wch: 5 }, // separación visual
            { wch: 5 }, { wch: 15 }, { wch: 17 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }
        ];

        XLSX.utils.book_append_sheet(wb, ws, 'Estadísticas');
        XLSX.writeFile(wb, 'Reporte de flotas por estadísticas.xlsx');
    };

    return (
        <div className='tab_content_container' style={{ gap: '0.5rem' }}>
            <div className='filters_container' style={{ marginBottom: '0.5rem' }}>
                <span>Filtrar por:</span>
                <Select
                    id='statsVehicleFilter'
                    value={vehicleFilter}
                    onChange={(e) => setVehicleFilter(e.target.value)}
                    displayEmpty
                    sx={selectStyles}
                >
                    <MenuItem value="" style={{ fontSize: '0.7rem' }}>Todos los vehículos</MenuItem>
                    {filteredDevices.map(device => (
                        <MenuItem key={device.id} value={device.label} style={{ fontSize: '0.7rem' }}>{device.label}</MenuItem>
                    ))}
                </Select>
                <Select
                    id='statsCheckpointFilter'
                    value={checkpointFilter}
                    onChange={(e) => setCheckpointFilter(e.target.value)}
                    displayEmpty
                    sx={selectStyles}
                >
                    <MenuItem value="" style={{ fontSize: '0.7rem' }}>Todos los puntos de control</MenuItem>
                    {userRoute.map(checkpoint => (
                        <MenuItem key={checkpoint.id + checkpoint.label} value={checkpoint.label} style={{ fontSize: '0.7rem' }}>{checkpoint.label}</MenuItem>
                    ))}
                </Select>
                <Select
                    id='statsStatusFilter'
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    displayEmpty
                    sx={selectStyles}
                >
                    <MenuItem value="" style={{ fontSize: '0.7rem' }}>Todos los estatus</MenuItem>
                    <MenuItem value="Atrasado" style={{ fontSize: '0.7rem' }}>Atrasado</MenuItem>
                    <MenuItem value="En tiempo" style={{ fontSize: '0.7rem' }}>En tiempo</MenuItem>
                    <MenuItem value="Adelantado" style={{ fontSize: '0.7rem' }}>Adelantado</MenuItem>
                </Select>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginLeft: 'auto' }}>
                    <BootstrapTooltip title="Descargar">
                        <Button
                            variant="contained"
                            color="success"
                            sx={{
                                padding: '0 0.8rem',
                                fontSize: '0.7rem',
                                color: 'white',
                                borderRadius: 'var(--border-radius-1)',
                                transition: 'all 0.3s ease',
                                height: '2rem',
                                minWidth: 'auto'
                            }}
                            onClick={handleClickDownload}
                        >
                            <span className="material-symbols-rounded" style={{ verticalAlign: 'middle', fontSize: '1rem' }}>download</span>
                        </Button>
                    </BootstrapTooltip>
                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <MenuItem
                            sx={{ fontSize: '0.8rem' }}
                            onClick={() => { handleExportPDF(); handleClose(); }}
                        >
                            Formato PDF
                        </MenuItem>
                        <MenuItem
                            sx={{ fontSize: '0.8rem' }}
                            onClick={() => { handleExportExcel(); handleClose(); }}
                        >
                            Formato XLSX
                        </MenuItem>
                    </Menu>
                </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {/* <h2>Estadísticas Generales</h2> */}
                <div className='grid_container'>
                    <div className='card' style={{ gridArea: 'total_events', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <span className="material-symbols-outlined">flag_check</span>
                        <div>
                            <h3>Total de eventos</h3>
                            <p>{stats.totalEvents}</p>
                        </div>
                    </div>
                    <div className='card' style={{ gridArea: 'puntuality', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        <span className="material-symbols-outlined">alarm_on</span>
                        <div>
                            <h3>Puntualidad</h3>
                            <p>{stats.punctualityRate}%</p>
                        </div>
                    </div>
                    <div className='card' style={{ gridArea: 'stats', display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                <span style={{
                                    display: 'inline-block',
                                    color: 'var(--color-success)',
                                    marginRight: '1rem',
                                    fontSize: '0.9rem'
                                }}>⬤</span>
                                <div>
                                    <h3>En tiempo</h3>
                                    <p>{stats.onTime.count} ({stats.totalStatusEvents > 0 ? (stats.onTime.count / stats.totalStatusEvents * 100).toFixed(2) + '%' : '- %'})</p>
                                </div>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3>Diferencia máxima</h3>
                                <p>{formatTimeToText(stats.onTime.maxDifference)}</p>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3>Tiempo acumulado</h3>
                                <p>{formatTimeToText(stats.onTime.totalTime)}</p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                <span style={{
                                    display: 'inline-block',
                                    color: 'var(--color-warning)',
                                    marginRight: '1rem',
                                    fontSize: '0.9rem'
                                }}>⬤</span>
                                <div>
                                    <h3>Adelantos</h3>
                                    <p>{stats.early.count} ({stats.totalStatusEvents > 0 ? (stats.early.count / stats.totalStatusEvents * 100).toFixed(2) + '%' : '- %'})</p>
                                </div>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3>Diferencia máxima</h3>
                                <p>{formatTimeToText(stats.early.maxDifference)}</p>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3>Tiempo acumulado</h3>
                                <p>{formatTimeToText(stats.early.totalTime)}</p>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                                <span style={{
                                    display: 'inline-block',
                                    color: 'var(--color-danger)',
                                    marginRight: '1rem',
                                    fontSize: '0.9rem'
                                }}>⬤</span>
                                <div>
                                    <h3>Atrasos</h3>
                                    <p>{stats.delayed.count} ({stats.totalStatusEvents > 0 ? (stats.delayed.count / stats.totalStatusEvents * 100).toFixed(2) + '%' : '- %'})</p>
                                </div>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3>Diferencia máxima</h3>
                                <p>{formatTimeToText(stats.delayed.maxDifference)}</p>
                            </div>
                            <div style={{ flex: 1 }}>
                                <h3>Tiempo acumulado</h3>
                                <p>{formatTimeToText(stats.delayed.totalTime)}</p>
                            </div>
                        </div>
                    </div>
                    <div style={{ gridArea: 'chart', }}>
                        <div className='card' style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            {/* <h3>Gráfica con los estados</h3> */}
                            <div style={{ flex: '1 1 auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <DonutChart stats={stats} />
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <h2>Estadísticas por vehículo</h2>
                    {vehicleData.length === 0 ?
                        <p style={{ textAlign: "center", fontSize: "14px", color: "#666" }}>No hay datos disponibles.</p>
                        :
                        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent', borderRadius: 'var(--card-border-radius)', marginTop: '1rem' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={tableCellHeaderStyle}>Vehículo</TableCell>
                                        <TableCell sx={tableCellHeaderStyle}>Horario</TableCell>
                                        <TableCell sx={tableCellHeaderStyle}>Tiempo Adelantado</TableCell>
                                        <TableCell sx={tableCellHeaderStyle}>Tiempo Puntual</TableCell>
                                        <TableCell sx={tableCellHeaderStyle}>Tiempo Atrasado</TableCell>
                                        <TableCell sx={tableCellHeaderStyle}>Tiempo Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {vehicleData.map((row, index) => (
                                        <TableRow key={index} sx={{ height: '2.3rem' }}>
                                            <TableCell sx={tableCellBodyStyle}>{row.vehicle}</TableCell>
                                            <TableCell sx={tableCellBodyStyle}>{row.schedule}</TableCell>
                                            <TableCell sx={tableCellBodyStyle}>{formatTimeToText(row.earlyTime)}</TableCell>
                                            <TableCell sx={tableCellBodyStyle}>{formatTimeToText(row.onTimeTime)}</TableCell>
                                            <TableCell sx={tableCellBodyStyle}>{formatTimeToText(row.delayedTime)}</TableCell>
                                            <TableCell sx={tableCellBodyStyle}>{formatTimeToText(row.totalTime)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>}
                </div>
            </div>
        </div>
    )
}

export default StatsTab